/**
 * Results table for 患者リスト(施術師毎) — Patient List by Practitioner.
 * 11 columns per BD spec; 生年月日/資格取得日/有効期限 rendered in 和暦 short form (eg H12.05.26).
 */

import React, { useMemo } from 'react';
import type { ColumnsType } from 'antd/es/table';
import { AppTable, AppPagination, AppTooltip } from '@/components/atoms';
import { PatientEditLink } from '@/components/molecules';
import { SERVICE_TYPE_A2, MASSAGER_LABEL_A2, MASSAGER_LABEL_R2 } from '@/constants/serviceType';
import { convGtJDateShort } from '@/utils/dateConverter';
import type { VisitPatientRow, VisitPatientPagination } from '../types';
import { EMPTY_TEXT_FILTERED } from '@/constants/messages';

interface Props {
  data: VisitPatientRow[];
  pagination: VisitPatientPagination | null;
  onPageChange: (page: number) => void;
  isLoading: boolean;
  serviceType: string;
}

type VisitPatientTableRow = VisitPatientRow & {
  tableRowKey: string;
};

const renderMassagerNames = (text: string) => {
  if (!text) return '';

  return text
    .split(/(\n|[^、,，\n]+[、,，]?)/)
    .filter(Boolean)
    .map((part, index) =>
      part === '\n' ? (
        <br key={`br-${index}`} />
      ) : (
        <span key={`${part}-${index}`} className="inline-block mr-1 whitespace-nowrap last:mr-0">
          {part.trim()}
        </span>
      )
    );
};

const VisitPatientTable: React.FC<Props> = ({
  data,
  pagination,
  onPageChange,
  isLoading,
  serviceType,
}) => {
  const isA2 = serviceType === SERVICE_TYPE_A2;
  const massagerColumnTitle = isA2 ? `${MASSAGER_LABEL_A2}名` : `${MASSAGER_LABEL_R2}名`;

  const rowStart = pagination ? (pagination.current_page - 1) * pagination.per_page : 0;

  const tableData = useMemo<VisitPatientTableRow[]>(
    () =>
      data.map((row, index) => ({
        ...row,
        tableRowKey: `${row.patient_id}-${row.qualification_date}-${row.expiration_date}-${rowStart + index}`,
      })),
    [data, rowStart]
  );

  const columns: ColumnsType<VisitPatientTableRow> = useMemo(
    () => [
      {
        title: 'No',
        key: 'no',
        width: 50,
        align: 'right',
        render: (_: unknown, __: VisitPatientTableRow, index: number) => rowStart + index + 1,
      },
      {
        title: '受診者番号',
        dataIndex: 'serial_number',
        key: 'serial_number',
        width: 110,
        render: (val: string | number, record: VisitPatientTableRow) => (
          <PatientEditLink patientId={record.patient_id} value={val || undefined} fallback="-" />
        ),
      },
      {
        title: '受診者名',
        dataIndex: 'name',
        key: 'name',
        width: 160,
        render: (text: string) => (
          <AppTooltip title={text} showOnlyOnOverflow>
            <div className="break-words line-clamp-1">{text}</div>
          </AppTooltip>
        ),
      },
      {
        title: '生年月日',
        dataIndex: 'date_of_birth',
        key: 'date_of_birth',
        width: 110,
        render: (value: string) => convGtJDateShort(value),
      },
      {
        title: '年齢',
        dataIndex: 'age',
        key: 'age',
        width: 60,
        align: 'right',
      },
      {
        title: '保険種別',
        dataIndex: 'insurer_type_name',
        key: 'insurer_type_name',
        width: 100,
        render: (text: string) => (
          <AppTooltip title={text} showOnlyOnOverflow>
            <div className="break-words line-clamp-1">{text}</div>
          </AppTooltip>
        ),
      },
      {
        title: '保険者名',
        dataIndex: 'insurer_name',
        key: 'insurer_name',
        width: 180,
        render: (text: string) => (
          <AppTooltip title={text} showOnlyOnOverflow>
            <div className="break-words line-clamp-1">{text}</div>
          </AppTooltip>
        ),
      },
      {
        title: '資格取得日',
        dataIndex: 'qualification_date',
        key: 'qualification_date',
        width: 110,
        render: (value: string) => convGtJDateShort(value),
      },
      {
        title: '有効期限',
        dataIndex: 'expiration_date',
        key: 'expiration_date',
        width: 110,
        render: (value: string) => convGtJDateShort(value),
      },
      {
        title: '施術師NO',
        dataIndex: 'massager_serial_numbers',
        key: 'massager_serial_numbers',
        width: 110,
        render: (text: string) => (
          <AppTooltip title={text} showOnlyOnOverflow>
            <div className="break-words line-clamp-1">{text}</div>
          </AppTooltip>
        ),
      },
      {
        title: massagerColumnTitle,
        dataIndex: 'massager_names',
        key: 'massager_names',
        width: 240,
        render: (text: string) => (
          <AppTooltip title={text} showOnlyOnOverflow>
            <div className="break-words line-clamp-1">{renderMassagerNames(text)}</div>
          </AppTooltip>
        ),
      },
    ],
    [rowStart, massagerColumnTitle]
  );

  return (
    <div>
      <AppTable<VisitPatientTableRow>
        columns={columns}
        dataSource={tableData}
        /* The backend groups by 9 columns (patient_id, serial_number, name,
           name_kana, date_of_birth, insurer_type, insurer_name, qualification_date,
           expiration_date — see VisitPatientRepository::buildSearchQuery), so one
           patient yields several rows. Two of those rows can share
           patient_id + qualification_date + expiration_date while differing only in
           name / name_kana (name_kana isn't even returned to the client), so the old
           3-part key collided. Duplicate React keys made AntD reuse stale row subtrees
           across page changes, leaving ghost rows when navigating back. No returned
           field set is guaranteed unique, so a page-local key is built before passing
           data to AntD. This avoids AntD's deprecated rowKey index parameter. */
        rowKey="tableRowKey"
        loading={isLoading}
        pagination={false}
        scroll={{ x: 'max-content' }}
        tableLayout="fixed"
        locale={{ emptyText: EMPTY_TEXT_FILTERED }}
      />

      <div className="flex justify-center mt-3">
        <AppPagination
          current={pagination?.current_page}
          total={pagination?.total}
          pageSize={pagination?.per_page}
          onChange={onPageChange}
          showSizeChanger={false}
        />
      </div>
    </div>
  );
};

export default VisitPatientTable;
